// @ts-nocheck -- Legacy JavaScript compatibility boundary; runtime behavior intentionally preserved.
import { useState } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import Image from "next/image";

import {
  AccountCircle as PersonIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from "@mui/icons-material";
import useTranslation from "next-translate/useTranslation";

import { Box, Grid, IconButton, Link, Menu } from "@/components/core";
import { useShortcutMenuStore } from "@/store/shortcut-menu";
import { useLogOutStore } from "@/store/login/apply";
import { NavbarButton } from "./navbar-button";
import TemporaryDrawer from "../drawer";

export function Navigations() {
  const { t } = useTranslation("common");
  const theme = useTheme();
  const mobileMediaQuery = theme.breakpoints.down("md");
  const isMobileDevice = useMediaQuery(mobileMediaQuery);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [profileAnchor, setProfileAnchor] = useState(null);
  const [contactAnchor, setContactAnchor] = useState(null);

  const shortcuts = useShortcutMenuStore((state) => state.shortcuts);
  const isMounted = useShortcutMenuStore((state) => state.isMounted);
  const logout = useLogOutStore((state) => state.logout);

  const handleLogout = async () => {
    const response = await fetch("/api/logout", {
      method: "POST",
    });

    const result = await response.json();

    if (result.success) {
      logout();

      window.location.href = "/login";
    }
  };

  const handleClick = (href, isExternal) => (e) => {
    e.preventDefault();

    if (typeof window !== "undefined") {
      if (isExternal) {
        window.open(href, "_blank", "noopener,noreferrer");
      } else {
        window.location.href = href;
      }
    }
  };

  return (
    <Box>
      <Grid
        sx={{
          justifyContent: { xs: "space-between", md: "flex-end" },
          paddingBottom: "1vh",
        }}
      >
        <Grid.Item
          xs={6}
          sx={{
            display: { xs: "block", md: "none" },
            marginLeft: { xs: 2, md: 0 },
          }}
        >
          <Link href="/" onClick={handleClick("/", false)}>
            <Image width={160} height={40} src="/svg/logo-white.svg" priority />
          </Link>
        </Grid.Item>
        <Grid.Item sx={{ display: "flex", zIndex: "1" }} mb={4}>
          <IconButton color="inherit" onClick={(e) => setProfileAnchor(e.currentTarget)}>
            <PersonIcon fontSize={!isMobileDevice ? "large" : "medium"} />
            <KeyboardArrowDownIcon />
          </IconButton>
          <Menu anchorEl={profileAnchor} onClose={() => setProfileAnchor(null)}>
            <Menu.Item
              onClick={() => {
                window.location.href = "/profile";
              }}
            >
              {t("navbar.profile")}
            </Menu.Item>
            <Menu.Item onClick={handleLogout}>{t("logout")}</Menu.Item>
          </Menu>
          <Grid.Item sx={{ display: { xs: "block", md: "none" } }}>
            <TemporaryDrawer />
          </Grid.Item>
        </Grid.Item>
      </Grid>
      {!isMobileDevice && (
        <Grid>
          <Grid.Item sx={{ zIndex: "99" }}>
            <Link href="/" onClick={handleClick("/", false)}>
              <Image width={200} height={50} src="/svg/logo-white.svg" priority />
            </Link>
          </Grid.Item>
          <Grid.Item sx={{ zIndex: "99" }}>
            <NavbarButton
              href="/confirm-cancel-process"
              onClick={handleClick("/confirm-cancel-process", false)}
              sx={{ border: "none" }}
            >
              <IconButton color="inherit">
                <Image src="/svg/savings.svg" alt="savingIcon" width={25} height={25} />
              </IconButton>
              {t("navbar.confirmStatus")}
            </NavbarButton>

            <NavbarButton
              href="/accounts"
              onClick={handleClick("/accounts", false)}
              sx={{ border: "none" }}
            >
              <IconButton color="inherit">
                <Image src="/svg/lastProcess.svg" alt="lastProcessIcon" width={25} height={25} />
              </IconButton>
              {t("navbar.transactions")}
            </NavbarButton>
          </Grid.Item>

          <Grid.Item sx={{ zIndex: "99" }}>
            <Grid spacing={2}>
              <Grid.Item>
                <NavbarButton
                  href="/money-transfer"
                  onClick={handleClick("/money-transfer", false)}
                >
                  {t("sendMoney")}
                </NavbarButton>
              </Grid.Item>
              <Grid.Item>
                <NavbarButton href="/accounts" onClick={handleClick("/accounts", false)}>
                  {t("accounts")}
                </NavbarButton>
              </Grid.Item>
              <Grid.Item>
                {isMounted && (
                  <>
                    <NavbarButton href="" onClick={(e) => setMenuAnchor(e.currentTarget)}>
                      {t("menu")}
                      <KeyboardArrowDownIcon sx={{ verticalAlign: "middle" }} />
                    </NavbarButton>

                    <Menu anchorEl={menuAnchor} onClose={() => setMenuAnchor(null)}>
                      {shortcuts
                        .filter((s) => s.group === "menu")
                        .map((shortcut) => (
                          <Menu.Item key={shortcut.id}>
                            <Link
                              href={shortcut.href ?? "#"}
                              key={shortcut.value}
                              onClick={handleClick(shortcut.href, shortcut.isExternal)}
                              {...(shortcut.isExternal && {
                                target: "_blank",
                                rel: "noopener noreferrer",
                              })}
                            >
                              <Grid spacing={2}>
                                <Grid.Item> {shortcut.icon}</Grid.Item>
                                <Grid.Item> {shortcut.name}</Grid.Item>
                              </Grid>
                            </Link>
                          </Menu.Item>
                        ))}
                    </Menu>
                  </>
                )}
              </Grid.Item>
              <Grid.Item>
                <NavbarButton href="#" onClick={(e) => setContactAnchor(e.currentTarget)}>
                  {t("contact")}
                  <KeyboardArrowDownIcon sx={{ verticalAlign: "middle" }} />
                </NavbarButton>
                <Menu anchorEl={contactAnchor} onClose={() => setContactAnchor()}>
                  {shortcuts
                    .filter((s) => s.group === "contact")
                    .map((shortcut) => (
                      <Menu.Item key={shortcut.id}>
                        <Link
                          href={shortcut.href ?? "#"}
                          onClick={handleClick(shortcut.href, shortcut.isExternal)}
                          key={shortcut.value}
                          {...(shortcut.isExternal && {
                            target: "_blank",
                            rel: "noopener noreferrer",
                          })}
                        >
                          <Grid spacing={1}>
                            <Grid.Item> {shortcut.icon}</Grid.Item>
                            <Grid.Item> {shortcut.name}</Grid.Item>
                          </Grid>
                        </Link>
                      </Menu.Item>
                    ))}
                </Menu>
              </Grid.Item>
            </Grid>
          </Grid.Item>
        </Grid>
      )}
    </Box>
  );
}
